import { useIntl } from 'react-intl';

import { useAppDispatch } from '@/shared/hooks';
import {
  debug,
  getCardRemovalAnimationDuration,
  trackEvent,
} from '@/shared/lib';

import { ModalID, modalModel } from '@/entities/modal';
import { notificationModel } from '@/entities/notification';

import { deleteConsultation } from '../model/transcriptorSlice.thunks';

type RemoveConsultationOptions = {
  /** Called when the remove request fails so the caller can roll back optimistic UI. */
  onError?: () => void;
};

/**
 * Resolves `true` as soon as the user confirms so the card can start its
 * removal animation; the request itself is sent after the animation. A failed
 * request shows an error notification and reports back through `onError`.
 */
export const useRemoveConsultation = () => {
  const { formatMessage } = useIntl();
  const dispatch = useAppDispatch();

  return async (
    consultationId: string,
    options?: RemoveConsultationOptions,
  ): Promise<boolean> =>
    new Promise((resolve) => {
      dispatch(
        modalModel.actions.openModal({
          modalID: ModalID.Confirm,
          data: {
            title: formatMessage({
              id: 'transcriptor.removeConsultation.title',
              defaultMessage: 'Remove consultation?',
            }),
            okText: formatMessage({
              id: 'global.applyRemove',
              defaultMessage: 'Yes, remove',
            }),
            description: formatMessage({
              id: 'transcriptor.removeConsultation.message',
              defaultMessage:
                'After removing, all consultation data will be lost. Are you sure?',
            }),
            danger: true,
            onConfirm: async () => {
              trackEvent({
                eventName:
                  'PatientProfilePageTranscriptorWidgetRemoveButtonClicked',
              });

              dispatch(modalModel.actions.closeModal(ModalID.Confirm));
              resolve(true);

              setTimeout(async () => {
                try {
                  await dispatch(deleteConsultation(consultationId)).unwrap();
                } catch (error) {
                  debug('Error removing consultation:', error);

                  dispatch(
                    notificationModel.actions.addNotification({
                      id: Date.now().toString(),
                      type: 'error',
                      message: formatMessage({
                        id: 'consultationOptions.delete.error',
                        defaultMessage:
                          'Failed to delete the consultation. Please try again.',
                      }),
                    }),
                  );
                  options?.onError?.();
                }
              }, getCardRemovalAnimationDuration());
            },
            onCancel: () => {
              resolve(false);
            },
          },
        }),
      );
    });
};
